import * as React from 'react';

export type ClassValue = string | number | boolean | undefined | null | { [key: string]: any } | ClassValue[];

function cn(...inputs: ClassValue[]): string {
  const classes: string[] = [];

  inputs.forEach((input) => {
    if (!input) return;

    if (typeof input === 'string' || typeof input === 'number') {
      classes.push(String(input));
    } else if (Array.isArray(input)) {
      const result = cn(...input);
      if (result) classes.push(result);
    } else if (typeof input === 'object') {
      Object.entries(input).forEach(([key, value]) => {
        if (value) classes.push(key);
      });
    }
  });

  return classes.join(' ');
}

interface TabsContextType {
  value: string;
  onValueChange: (value: string) => void;
}

const TabsContext = React.createContext<TabsContextType | undefined>(undefined);

interface TabsProps extends React.HTMLAttributes<HTMLDivElement> {
  value: string;
  onValueChange: (value: string) => void;
}

const Tabs = React.forwardRef<HTMLDivElement, TabsProps>(
  ({ className, value, onValueChange, children, id, style }, ref) => {
    const contextValue = React.useMemo(() => ({ value, onValueChange }), [value, onValueChange]);

    return (
      <TabsContext.Provider value={contextValue}>
        <div ref={ref} className={cn('w-full', className)} id={id} style={style}>
          {children}
        </div>
      </TabsContext.Provider>
    );
  },
);
Tabs.displayName = 'Tabs';

const TabsList = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  // eslint-disable-next-line react/prop-types
  ({ className, id, style, children }, ref) => (
    <div
      ref={ref}
      className={cn('inline-flex h-10 items-center justify-center rounded-md bg-gray-100 p-1 text-gray-600', className)}
      id={id}
      style={style}>
      {children}
    </div>
  ),
);
TabsList.displayName = 'TabsList';

interface TabsTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  value: string;
}

const TabsTrigger = React.forwardRef<HTMLButtonElement, TabsTriggerProps>(
  ({ className, value, children, onClick, id, style, disabled, tabIndex, type = 'button' }, ref) => {
    const context = React.useContext(TabsContext);

    if (!context) {
      console.error('TabsTrigger must be used within a Tabs component');
      return null;
    }

    const { value: activeValue, onValueChange } = context;
    const isActive = activeValue === value;

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      try {
        onValueChange(value);
        onClick?.(event);
      } catch (error) {
        console.error('Error handling tab click:', error);
      }
    };

    return (
      <button
        ref={ref}
        type="button"
        role="tab"
        aria-selected={isActive}
        data-state={isActive ? 'active' : 'inactive'}
        className={cn(
          'inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium transition-all focus-visible:outline-none focus:ring-2 focus:ring-blue-500 disabled:pointer-events-none disabled:opacity-50',
          isActive ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900',
          className,
        )}
        onClick={handleClick}
        id={id}
        style={style}
        disabled={disabled}
        tabIndex={tabIndex}>
        {children}
      </button>
    );
  },
);
TabsTrigger.displayName = 'TabsTrigger';

interface TabsContentProps extends React.HTMLAttributes<HTMLDivElement> {
  value: string;
}

const TabsContent = React.forwardRef<HTMLDivElement, TabsContentProps>(
  ({ className, value, children, id, style }, ref) => {
    const context = React.useContext(TabsContext);

    if (!context) {
      console.error('TabsContent must be used within a Tabs component');
      return null;
    }

    const { value: activeValue } = context;
    const isActive = activeValue === value;

    if (!isActive) return null;

    return (
      <div
        ref={ref}
        role="tabpanel"
        data-state={isActive ? 'active' : 'inactive'}
        className={cn('mt-2 focus-visible:outline-none focus:ring-2 focus:ring-blue-500', className)}
        id={id}
        style={style}>
        {children}
      </div>
    );
  },
);
TabsContent.displayName = 'TabsContent';

export { Tabs, TabsList, TabsTrigger, TabsContent };
